import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { sendOTP, verifyOTP, setOtpSent } from '../../store/authSlice';

const vdaImage  = '/assets/banner.png';
const logoImage = '/assets/logo.png';

const NOC_STEPS = [
  { icon: '📝', num: '01', title: 'Apply Online',   desc: 'Submit NOC application with required documents' },
  { icon: '🏢', num: '02', title: 'Dept Review',    desc: 'Related department reviews your application' },
  { icon: '✅', num: '03', title: 'Final Approval', desc: 'Final authority approves and issues NOC' },
  { icon: '⬇️', num: '04', title: 'Download NOC',  desc: 'Download your approved NOC certificate' },
];

export default function CitizenLoginPage() {
  const [mobile, setMobile]               = useState('');
  const [otp, setOtp]                     = useState('');
  const [notRegistered, setNotRegistered] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, otpSent, role } = useSelector(s => s.auth);

  React.useEffect(() => {
    if (role === 'citizen') navigate('/user/dashboard');
  }, [role, navigate]);

  const handleSendOTP = async (e) => {
    e?.preventDefault();
    setNotRegistered(false);
    const res = await dispatch(sendOTP(mobile));
    if (res.meta.requestStatus === 'rejected') {
      if (res.payload?.includes('not registered') || res.error?.message?.includes('404')) {
        setNotRegistered(true);
      }
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const res = await dispatch(verifyOTP({ mobile, otp }));
    if (res.meta.requestStatus === 'fulfilled') navigate('/user/dashboard');
  };

  return (
    <div className="min-h-screen flex">

      {/* ── LEFT — Info Panel (same as LoginPage) ─────────── */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative flex-col"
        style={{ background: 'linear-gradient(135deg, #1a365d 0%, #2b6cb0 60%, #1e40af 100%)' }}>

        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />

        <div className="relative z-10 flex flex-col h-full p-10">

          {/* Banner Image */}
          <div className="rounded-2xl overflow-hidden shadow-2xl mb-8 flex-shrink-0" style={{ maxHeight: '260px' }}>
            <img src={vdaImage} alt="VDA" className="w-full h-full object-cover"
              onError={e => {
                e.target.parentNode.style.background = '#c53030';
                e.target.parentNode.innerHTML =
                  '<div class="flex flex-col items-center justify-center h-52 text-white">' +
                  '<span class="font-black text-4xl">VDA</span>' +
                  '<span class="text-base mt-2 font-semibold">NOC Management System</span>' +
                  '<span class="text-sm mt-1 opacity-80">Varanasi Development Authority</span>' +
                  '</div>';
              }} />
          </div>

          {/* How NOC Works */}
          <div className="mb-6">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4 opacity-80">
              How NOC Application Works
            </h3>
            <div className="space-y-3">
              {NOC_STEPS.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
                    {step.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm">{step.title}</p>
                    <p className="text-blue-200 text-xs">{step.desc}</p>
                  </div>
                  <div className="w-7 h-7 bg-white/10 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {step.num}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Application Process */}
          <div className="mt-auto">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4 opacity-80">
              Application Process
            </h3>
            <div className="flex gap-3">
              {NOC_STEPS.map((step, i) => (
                <div key={i} className="flex-1 bg-white/10 rounded-xl p-3 text-center">
                  <div className="text-2xl mb-1">{step.icon}</div>
                  <p className="text-white font-bold text-xs">{step.num}</p>
                  <p className="text-white font-semibold text-xs mt-0.5">{step.title}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-2">
              {NOC_STEPS.map((step, i) => (
                <div key={i} className="flex-1 text-center">
                  <p className="text-blue-200 text-xs leading-tight">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* ── RIGHT — Login Form ────────────────────────────── */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-sm">

          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-3 overflow-hidden">
              <img src={logoImage} alt="VDA" className="w-12 h-12 object-contain"
                 />
            </div>
            <h1 className="text-xl font-bold text-gray-800">Citizen Portal</h1>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Citizen Login</h2>
              <p className="text-gray-500 text-sm mt-1">Login with your registered mobile number</p>
            </div>

            {notRegistered && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm font-semibold text-red-700 mb-1">📵 Mobile not registered!</p>
                <p className="text-xs text-red-600 mb-3">This number is not registered. Please register first.</p>
                <Link to="/register"
                  className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-4 py-2 rounded-lg">
                  📝 Register Now
                </Link>
              </div>
            )}

            {!otpSent ? (
              <form onSubmit={handleSendOTP} className="space-y-5">
                <div>
                  <label className="form-label">Mobile Number</label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 text-gray-500 text-sm font-medium">
                      +91
                    </span>
                    <input type="tel" className="form-input rounded-l-none flex-1"
                      placeholder="10-digit mobile number"
                      value={mobile}
                      onChange={e => { setMobile(e.target.value.replace(/\D/,'').slice(0,10)); setNotRegistered(false); }}
                      maxLength={10} required autoFocus />
                  </div>
                  {mobile.length > 0 && mobile.length < 10 && (
                    <p className="text-xs text-orange-500 mt-1">{mobile.length}/10 digits</p>
                  )}
                </div>
                <button type="submit" disabled={loading || mobile.length !== 10}
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #1a365d, #2b6cb0)' }}>
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending OTP...
                    </span>
                  ) : '📱 Send OTP'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerify} className="space-y-5">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between">
                  <span className="text-sm text-blue-700">OTP sent to +91 {mobile}</span>
                  <button type="button" className="text-xs text-blue-700 underline font-medium"
                    onClick={() => { dispatch(setOtpSent(false)); setOtp(''); }}>
                    Change
                  </button>
                </div>
                <div>
                  <label className="form-label">Enter OTP</label>
                  <input type="text"
                    className="form-input text-center text-3xl font-bold tracking-[1rem]"
                    placeholder="• • • •" value={otp}
                    onChange={e => setOtp(e.target.value.replace(/\D/,'').slice(0,6))}
                    maxLength={6} required autoFocus />
                </div>
                <button type="submit" disabled={loading || otp.length < 4}
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #1a365d, #2b6cb0)' }}>
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Verifying...
                    </span>
                  ) : '✓ Verify & Login'}
                </button>
                <button type="button" onClick={handleSendOTP}
                  className="w-full text-sm text-gray-500 hover:text-gray-700 py-1">
                  🔄 Resend OTP
                </button>
              </form>
            )}

            <div className="mt-6 pt-5 border-t border-gray-100 space-y-3 text-center">
              <Link to="/register"
                className="block text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                📝 New User? Register here →
              </Link>
              <Link to="/login"
                className="block text-xs text-gray-400 hover:text-gray-600 hover:underline">
                Employee / Official Login
              </Link>
            </div>
          </div>

          <p className="text-center text-gray-400 text-xs mt-5">
            © {new Date().getFullYear()} Varanasi Development Authority
          </p>
        </div>
      </div>
    </div>
  );
}